/**
 * Admin Notification Routes Configuration
 * Maps notification events to admin portal routes for deep linking
 */

/**
 * Complete route mapping for all notification events
 * This defines where each notification should navigate when clicked
 */
export const ADMIN_NOTIFICATION_ROUTES = {
  // Dashboard
  'dashboard': {
    web: '/dashboard',
    description: 'Navigate to dashboard',
    icon: 'dashboard',
    color: 'blue',
  },

  // ===== APPOINTMENTS =====
  'appointment.booked': {
    web: '/admin/appointments',
    params: [],
    icon: 'calendar',
    color: 'blue',
    priority: 'medium',
    action: 'view',
    duration: 4,
  },
  'appointment.confirmed': {
    web: '/admin/appointments',
    params: ['appointment_id'],
    icon: 'check-circle',
    color: 'green',
    priority: 'medium',
    action: 'confirm',
    duration: 4,
  },
  'appointment.cancelled': {
    web: '/admin/appointments',
    params: [],
    icon: 'close-circle',
    color: 'red',
    priority: 'high',
    action: 'view',
    duration: 5,
  },
  'appointment.converted': {
    web: '/checkins',
    params: ['checkin_id'],
    icon: 'user-add',
    color: 'blue',
    priority: 'medium',
    action: 'view',
    duration: 4,
  },
  'appointment.expired': {
    web: '/admin/appointments',
    params: [],
    icon: 'clock-circle',
    color: 'gray',
    priority: 'low',
    action: 'view',
    duration: 3,
  },

  // ===== PA (PRE-AUTHORIZATION) =====
  'pa.created': {
    web: '/preauth',
    params: ['pa_id'],
    icon: 'file-text',
    color: 'orange',
    priority: 'medium',
    action: 'review',
    duration: 4,
  },
  'pa.approved': {
    web: '/preauth',
    params: ['pa_id'],
    icon: 'check-circle',
    color: 'green',
    priority: 'high',
    action: 'view',
    duration: 5,
  },
  'pa.rejected': {
    web: '/preauth',
    params: ['pa_id'],
    icon: 'close-circle',
    color: 'red',
    priority: 'high',
    action: 'review',
    duration: 5,
  },
  'pa.query_responded': {
    web: '/preauth',
    params: ['pa_id'],
    icon: 'message',
    color: 'purple',
    priority: 'high',
    action: 'respond',
    duration: 5,
  },
  'pa.flagged': {
    web: '/preauth',
    params: ['pa_id'],
    icon: 'exclamation-circle',
    color: 'orange',
    priority: 'high',
    action: 'review',
    duration: 5,
  },

  // ===== ENCOUNTERS / CHECK-INS =====
  'encounter.created': {
    web: '/checkins',
    params: ['checkin_id'],
    icon: 'user-add',
    color: 'blue',
    priority: 'medium',
    action: 'view',
    duration: 4,
  },
  'encounter.updated': {
    web: '/checkins',
    params: ['checkin_id'],
    icon: 'edit',
    color: 'blue',
    priority: 'medium',
    action: 'view',
    duration: 4,
  },

  // ===== CLAIMS =====
  'claim.paid': {
    web: '/claims',
    params: ['claim_id'],
    icon: 'dollar',
    color: 'green',
    priority: 'high',
    action: 'view',
    duration: 5,
  },
  'claim.approved': {
    web: '/claims',
    params: ['claim_id'],
    icon: 'check-circle',
    color: 'green',
    priority: 'high',
    action: 'view',
    duration: 5,
  },
  'claim.rejected': {
    web: '/claims',
    params: ['claim_id'],
    icon: 'close-circle',
    color: 'red',
    priority: 'high',
    action: 'review',
    duration: 5,
  },
  // Spec §10: facility finished an encounter month's claims. Opens that
  // facility/month in Claims Processing (route is built server-side too).
  'claim.month_submitted': {
    web: '/claims',
    params: ['month', 'facility_id'],
    icon: 'check-circle',
    color: 'green',
    priority: 'medium',
    action: 'view',
    duration: 6,
  },

  // ===== SYSTEM =====
  'system.alert': {
    web: '/announcements',
    params: [],
    icon: 'warning',
    color: 'red',
    priority: 'urgent',
    action: 'view',
    duration: 8,
  },
  'system.maintenance': {
    web: '/announcements',
    params: [],
    icon: 'tool',
    color: 'orange',
    priority: 'urgent',
    action: 'view',
    duration: 8,
  },
  'system.app_update': {
    web: '/announcements',
    params: [],
    icon: 'download',
    color: 'blue',
    priority: 'medium',
    action: 'view',
    duration: 5,
  },
  'system.emergency': {
    web: '/announcements',
    params: [],
    icon: 'warning',
    color: 'red',
    priority: 'urgent',
    action: 'view',
    duration: 8,
  },
  'system.broadcast': {
    web: '/announcements',
    params: [],
    icon: 'notification',
    color: 'blue',
    priority: 'low',
    action: 'view',
    duration: 4,
  },

  // ===== STAFF UPLOADS (HR → care admin) =====
  'staff_upload_updated': {
    web: '/corporations/staff-uploads',
    params: ['batch_id', 'org_id'],
    icon: 'team',
    color: 'blue',
    priority: 'medium',
    action: 'review',
    duration: 4,
  },
};

/**
 * Get route configuration for a specific event type
 */
export function getRouteForEvent(eventType) {
  return ADMIN_NOTIFICATION_ROUTES[eventType];
}

/**
 * Check if an event type has a configured route
 */
export function hasRouteForEvent(eventType) {
  return eventType in ADMIN_NOTIFICATION_ROUTES;
}

/**
 * Get all event types with routes
 */
export function getAllRoutableEvents() {
  return Object.keys(ADMIN_NOTIFICATION_ROUTES);
}

/**
 * Get routes by category
 */
export function getRoutesByCategory(category) {
  return Object.entries(ADMIN_NOTIFICATION_ROUTES)
    .filter(([key]) => key.startsWith(category.split('.')[0]))
    .map(([, route]) => route);
}

/**
 * Priority-based color mapping for UI
 */
export const PRIORITY_COLORS = {
  urgent: '#ff4d4f', // red
  high: '#faad14',   // orange
  medium: '#1890ff',  // blue
  low: '#8c8c8c',    // gray
};

/**
 * Priority-based icon color mapping
 */
export const PRIORITY_ICON_COLORS = {
  urgent: 'red',
  high: 'orange',
  medium: 'blue',
  low: 'gray',
};

/**
 * Get color for priority level
 */
export function getPriorityColor(priority) {
  return PRIORITY_COLORS[priority] || PRIORITY_COLORS.medium;
}

/**
 * Get icon color for priority level
 */
export function getPriorityIconColor(priority) {
  return PRIORITY_ICON_COLORS[priority] || PRIORITY_ICON_COLORS.medium;
}

/**
 * Default export for backward compatibility
 */
export default ADMIN_NOTIFICATION_ROUTES;

